import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import logoImage from '../static/caledoniaLogo.png';

const eventID = '6a0d871751beac0014246bc6';
const adminTokenStorageKey = 'caledoniaAdminToken';
const buttonStyles = {
  background: '#123a49',
  color: 'white',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { background: '#668491' },
};

const columns = [
  { key: 'createdAt', label: 'Submitted At', format: formatDateTime },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'company', label: 'Company' },
  { key: 'country', label: 'Country' },
  { key: 'type', label: 'Type' },
  { key: 'attending', label: 'Attendance' },
];

function formatDateTime(value) {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function normaliseValue(value) {
  return String(value || '').toLowerCase();
}

function formatAttendance(value) {
  const labels = {
    inperson: 'In Person',
    virtual: 'Online',
    unabletoattend: 'Unable to attend',
  };

  return labels[value] || value || '';
}

function getRowDisplayValues(registrant) {
  return columns.reduce((acc, column) => {
    const rawValue = registrant[column.key];
    acc[column.key] = column.format ? column.format(rawValue) : String(rawValue || '');

    if (column.key === 'attending') {
      acc[column.key] = formatAttendance(rawValue);
    }

    return acc;
  }, {});
}

function getAttendanceChipColor(value) {
  if (value === 'inperson') return { background: '#e6f2ef', color: '#0f5132' };
  if (value === 'virtual') return { background: '#e8f0f7', color: '#123a49' };
  return { background: '#f4eded', color: '#7a2e2e' };
}

export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(adminTokenStorageKey) || '');
  const [registrants, setRegistrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [filters, setFilters] = useState(
    columns.reduce((acc, column) => ({ ...acc, [column.key]: '' }), {})
  );
  const [selectedFilterField, setSelectedFilterField] = useState('name');
  const [filterDraft, setFilterDraft] = useState('');

  const isLoggedIn = Boolean(adminToken);

  const filteredRegistrants = useMemo(() => {
    const searchTerm = normaliseValue(globalSearch);

    return registrants.filter((registrant) => {
      const row = getRowDisplayValues(registrant);

      const matchesGlobalSearch = !searchTerm || Object.values(row).some((value) => (
        normaliseValue(value).includes(searchTerm)
      ));

      const matchesColumnFilters = columns.every((column) => {
        const filterValue = normaliseValue(filters[column.key]);
        return !filterValue || normaliseValue(row[column.key]).includes(filterValue);
      });

      return matchesGlobalSearch && matchesColumnFilters;
    });
  }, [filters, globalSearch, registrants]);

  const activeFilterCount = useMemo(() => (
    Object.values(filters).filter(Boolean).length + (globalSearch ? 1 : 0)
  ), [filters, globalSearch]);

  async function handleLogin(event) {
    event.preventDefault();
    setError('');
    setLoginLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/registrant/admin/login/?token=${process.env.REACT_APP_API_BASE_TOKEN}`,
        { password }
      );

      if (!response.data.success || !response.data.token) {
        setError(response.data.message || 'Unable to log in');
        return;
      }

      sessionStorage.setItem(adminTokenStorageKey, response.data.token);
      setAdminToken(response.data.token);
      setPassword('');
    } catch (error) {
      setError(error.response?.data?.message || 'Unable to log in');
    } finally {
      setLoginLoading(false);
    }
  }

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(adminTokenStorageKey);
    setAdminToken('');
    setRegistrants([]);
  }, []);

  const fetchRegistrants = useCallback(async (token) => {
    setError('');
    setLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/registrant/admin/inPersonRegistrants/?token=${process.env.REACT_APP_API_BASE_TOKEN}&eventID=${eventID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.data.success) {
        setError(response.data.message || 'Unable to load registrants');
        return;
      }

      setRegistrants(response.data.registrants || []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
        setError('Admin session expired. Please log in again.');
      } else {
        setError(error.response?.data?.message || 'Unable to load registrants');
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!adminToken) return;

    fetchRegistrants(adminToken);
  }, [adminToken, fetchRegistrants]);

  function handleFilterChange(key, value) {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  }

  function addFilter() {
    if (!filterDraft.trim()) return;

    handleFilterChange(selectedFilterField, filterDraft.trim());
    setFilterDraft('');
  }

  function removeFilter(key) {
    handleFilterChange(key, '');
  }

  function clearFilters() {
    setGlobalSearch('');
    setFilters(columns.reduce((acc, column) => ({ ...acc, [column.key]: '' }), {}));
    setFilterDraft('');
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#eef3f4', color: '#123a49' }}>
      <CssBaseline />
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 2, md: 4 } }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              p: { xs: 2, md: 3 },
              borderRadius: '8px',
              border: '1px solid rgba(18, 58, 73, 0.12)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <img src={logoImage} alt="Caledonia Mining" style={{ maxWidth: '220px', width: '54%', height: '58px', objectFit: 'contain', mixBlendMode: 'multiply' }} />
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography component="h1" sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.15 }}>
                  Registration Admin
                </Typography>
                <Typography sx={{ color: '#45626d' }}>
                  Caledonia Mining CMD 2026
                </Typography>
              </Box>
            </Box>
            {isLoggedIn && (
              <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button onClick={() => fetchRegistrants(adminToken)} disabled={loading} sx={{ ...buttonStyles, width: { xs: '100%', sm: 'auto' } }}>
                  Refresh
                </Button>
                <Button onClick={handleLogout} sx={{ ...buttonStyles, width: { xs: '100%', sm: 'auto' } }}>
                  Logout
                </Button>
              </Stack>
            )}
          </Paper>

          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Typography component="h1" sx={{ fontSize: '1.55rem', fontWeight: 700 }}>
              Registration Admin
            </Typography>
            <Typography sx={{ color: '#45626d' }}>
              Caledonia Mining CMD 2026 registrations
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          {!isLoggedIn ? (
            <Paper
              elevation={0}
              sx={{
                maxWidth: 440,
                width: '100%',
                p: { xs: 2, sm: 3 },
                borderRadius: '8px',
                border: '1px solid rgba(18, 58, 73, 0.12)',
              }}
            >
              <Stack component="form" spacing={2} onSubmit={handleLogin}>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                  Admin Login
                </Typography>
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  fullWidth
                />
                <Button type="submit" disabled={loginLoading} sx={buttonStyles}>
                  {loginLoading ? 'Logging in...' : 'Login'}
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                }}
              >
                <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid rgba(18, 58, 73, 0.12)' }}>
                  <Typography sx={{ color: '#45626d', fontSize: '0.85rem' }}>Total registrations</Typography>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>{registrants.length}</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid rgba(18, 58, 73, 0.12)' }}>
                  <Typography sx={{ color: '#45626d', fontSize: '0.85rem' }}>Visible rows</Typography>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>{filteredRegistrants.length}</Typography>
                </Paper>
                <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', border: '1px solid rgba(18, 58, 73, 0.12)' }}>
                  <Typography sx={{ color: '#45626d', fontSize: '0.85rem' }}>Active filters</Typography>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 700 }}>{activeFilterCount}</Typography>
                </Paper>
              </Box>

              <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: '8px', border: '1px solid rgba(18, 58, 73, 0.12)' }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                        Registrations
                      </Typography>
                      <Typography sx={{ color: '#45626d', fontSize: '0.9rem' }}>
                        Search and filter the registration data below.
                      </Typography>
                    </Box>
                    {activeFilterCount > 0 && (
                      <Button onClick={clearFilters} variant="outlined" sx={{ borderColor: '#123a49', color: '#123a49', textTransform: 'none', borderRadius: '8px' }}>
                        Clear filters
                      </Button>
                    )}
                  </Box>

                  <TextField
                    label="Search all fields"
                    value={globalSearch}
                    onChange={(event) => setGlobalSearch(event.target.value)}
                    fullWidth
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexDirection: { xs: 'column', md: 'row' },
                      alignItems: { xs: 'stretch', md: 'center' },
                    }}
                  >
                    <TextField
                      select
                      label="Filter field"
                      value={selectedFilterField}
                      onChange={(event) => setSelectedFilterField(event.target.value)}
                      size="small"
                      sx={{ minWidth: { xs: '100%', md: 220 } }}
                    >
                      {columns.map((column) => (
                        <MenuItem key={column.key} value={column.key}>
                          {column.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label={`Filter ${columns.find((column) => column.key === selectedFilterField)?.label || 'field'}`}
                      value={filterDraft}
                      onChange={(event) => setFilterDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addFilter();
                        }
                      }}
                      size="small"
                      fullWidth
                    />
                    <Button onClick={addFilter} disabled={!filterDraft.trim()} sx={{ ...buttonStyles, minWidth: { xs: '100%', md: 120 } }}>
                      Add filter
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', minHeight: 32 }}>
                    {columns.map((column) => (
                      filters[column.key] ? (
                        <Chip
                          key={`${column.key}-chip`}
                          label={`${column.label}: ${filters[column.key]}`}
                          onDelete={() => removeFilter(column.key)}
                          sx={{ background: '#e8f0f7', color: '#123a49', fontWeight: 600 }}
                        />
                      ) : null
                    ))}
                    {!Object.values(filters).some(Boolean) && (
                      <Typography sx={{ color: '#6a7f87', fontSize: '0.9rem', alignSelf: 'center' }}>
                        No field filters applied.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ borderRadius: '8px', border: '1px solid rgba(18, 58, 73, 0.12)', overflow: 'hidden' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {columns.map((column) => (
                              <TableCell key={column.key} sx={{ fontWeight: 700, color: '#123a49', minWidth: 150, background: '#f7fafb' }}>
                                {column.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredRegistrants.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={columns.length} align="center" sx={{ py: 5, color: '#45626d' }}>
                                No registrations found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredRegistrants.map((registrant) => {
                              const row = getRowDisplayValues(registrant);

                              return (
                                <TableRow key={registrant._id} hover>
                                  {columns.map((column) => (
                                    <TableCell key={`${registrant._id}-${column.key}`}>
                                      {column.key === 'attending' ? (
                                        <Chip
                                          label={row[column.key]}
                                          size="small"
                                          sx={{ ...getAttendanceChipColor(registrant.attending), fontWeight: 600 }}
                                        />
                                      ) : row[column.key]}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' }, p: 1.5 }}>
                      {filteredRegistrants.length === 0 ? (
                        <Box sx={{ py: 5, textAlign: 'center', color: '#45626d' }}>
                          No registrations found.
                        </Box>
                      ) : (
                        filteredRegistrants.map((registrant) => {
                          const row = getRowDisplayValues(registrant);

                          return (
                            <Paper
                              key={registrant._id}
                              elevation={0}
                              sx={{ p: 2, borderRadius: '8px', border: '1px solid rgba(18, 58, 73, 0.12)' }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                                <Box>
                                  <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>{row.name || 'Unnamed'}</Typography>
                                  <Typography sx={{ color: '#45626d', fontSize: '0.9rem', wordBreak: 'break-word' }}>{row.email}</Typography>
                                </Box>
                                <Chip
                                  label={row.attending}
                                  size="small"
                                  sx={{ ...getAttendanceChipColor(registrant.attending), fontWeight: 600, flexShrink: 0 }}
                                />
                              </Box>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                              {columns.map((column) => (
                                column.key !== 'name' && column.key !== 'email' && column.key !== 'attending' ? (
                                  <Box key={`${registrant._id}-${column.key}`}>
                                    <Typography sx={{ color: '#6a7f87', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: 0 }}>
                                      {column.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.92rem', wordBreak: 'break-word' }}>{row[column.key] || '-'}</Typography>
                                  </Box>
                                ) : null
                              ))}
                              </Box>
                            </Paper>
                          );
                        })
                      )}
                    </Stack>
                  </>
                )}
              </Paper>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
